import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { usePageMeta } from '../lib/usePageMeta';
import { MailCheck, Loader2, KeyRound, ArrowLeft } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const RecoverPilotLinkPage = () => {
  usePageMeta(
    'Request a new early-access link — ResiAX',
    'Request a fresh secure link for your ResiAX resident early-access registration.'
  );
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  React.useEffect(() => { window.scrollTo(0, 0); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await fetch(`${BACKEND_URL}/api/pilot-handoff/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } catch (err) {
      // Neutral outcome regardless — never reveal registration status
    }
    setIsSubmitting(false);
    setDone(true);
  };

  return (
    <div className="min-h-screen bg-[#FFFAF8]">
      <Header />
      <main className="pt-32 pb-20">
        <div className="max-w-md mx-auto px-4 sm:px-6">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-[#B5541B]/10 px-4 py-2 rounded-full mb-5">
              <KeyRound className="w-4 h-4 text-[#B5541B]" />
              <span className="text-sm font-semibold text-[#B5541B]">Link recovery</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#1F1E1E] mb-3">Request a new early-access link</h1>
            <p className="text-base text-[#3B3535]/70 leading-relaxed">
              If your secure link expired or was already opened, enter the email you registered
              with and we&apos;ll send you a fresh one. Links work once and last 2 hours.
            </p>
          </div>

          {done ? (
            <div data-testid="recover-success" className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 text-center">
              <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <MailCheck className="w-7 h-7 text-green-600" />
              </div>
              <p className="text-[#1F1E1E] font-semibold mb-2">Check your inbox</p>
              <p className="text-sm text-[#3B3535]/70 leading-relaxed">
                If a matching registration exists, we&apos;ve emailed a fresh secure link.
                Don&apos;t forget to check your spam folder.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 space-y-4" data-testid="recover-form">
              <div className="space-y-2">
                <Label htmlFor="recover-email" className="text-[#3B3535] font-medium">Registered email <span className="text-[#B5541B]">*</span></Label>
                <Input
                  id="recover-email"
                  data-testid="recover-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="border-gray-200 focus:border-[#B5541B] focus:ring-[#B5541B]"
                />
              </div>
              <Button
                type="submit"
                data-testid="recover-submit-btn"
                disabled={isSubmitting || !email}
                className="w-full bg-[#B5541B] hover:bg-[#8B3F14] text-white font-semibold py-5 rounded-full shadow-md transition-all duration-300 disabled:opacity-50"
              >
                {isSubmitting ? (<><Loader2 className="mr-2 w-4 h-4 animate-spin" />Sending...</>) : 'Email me a new link'}
              </Button>
            </form>
          )}

          <div className="text-center mt-6">
            <Link to="/resident-pilot" className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#B5541B] hover:text-[#8B3F14] transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Back to the resident pilot
            </Link>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default RecoverPilotLinkPage;
